/**
 * IntegrationsTab.jsx — Settings Integrations tab
 *
 * Google Calendar integration is functional (when configured).
 * Other integrations are stubs for future development.
 */

import { cn } from '../../lib/utils';
import { useGcalConfig, useGcalSetup } from '../../hooks/useGcalSync';
import { useXeroStatus, useXeroDisconnect, useXeroAccounts } from '../../hooks/useXero';
import { api } from '../../lib/api';
import { logAction } from '../../lib/actionLogger';
import { useToast } from '../layout/Toast';
import { useFocus } from '../../hooks/useFocus';
import { useState, useEffect, useCallback } from 'react';

const INTEGRATIONS = [
  {
    id: 'xero',
    name: 'Xero Accounting',
    description: 'Sync invoices and financial data with Xero. Auto-reconcile payments and generate reports.',
    status: 'dynamic',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="3" width="20" height="18" rx="3" />
        <path d="M7 8l3 4-3 4M14 8l3 4-3 4" />
      </svg>
    ),
  },
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Send quotes, invoices, and notifications directly from RFOS through your Gmail account.',
    status: 'coming_soon',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="4" width="20" height="16" rx="3" />
        <path d="M2 7l10 7 10-7" />
      </svg>
    ),
  },
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    description: 'Push shoot dates to a shared RFOS calendar. Crew members are added as attendees.',
    status: 'dynamic', // handled separately
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <line x1="9" y1="2" x2="9" y2="6" />
        <line x1="15" y1="2" x2="15" y2="6" />
      </svg>
    ),
  },
  {
    id: 'dropbox',
    name: 'Dropbox / Google Drive',
    description: 'Link file deliveries and contracts to cloud storage for easy access and sharing.',
    status: 'coming_soon',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M20 16.2A4.5 4.5 0 0018 8h-1.26A7 7 0 104 14.93" />
        <polyline points="16 16 12 12 8 16" />
        <line x1="12" y1="12" x2="12" y2="21" />
      </svg>
    ),
  },
];

const STATUS_BADGE = {
  connected: { label: 'Connected', classes: 'bg-[#1e8b4a]/10 border-[#1e8b4a]/30 text-[#1e8b4a]' },
  coming_soon: { label: 'Coming Soon', classes: 'bg-surface-2 border-border text-text-dim' },
};

export default function IntegrationsTab() {
  const { data: gcalConfig, isLoading: gcalLoading } = useGcalConfig();
  const gcalSetup = useGcalSetup();
  const { data: xeroStatus, isLoading: xeroLoading, refetch: xeroRefetch } = useXeroStatus();
  const xeroConnected = xeroStatus?.connected === true;
  const xeroDisconnect = useXeroDisconnect();
  const { data: xeroAccounts = [] } = useXeroAccounts(xeroConnected);
  const { toast } = useToast();
  const { isHidden } = useFocus();
  const [xeroConnecting, setXeroConnecting] = useState(false);

  const gcalConnected = gcalConfig?.configured === true;
  const gcalCalendarId = gcalConfig?.calendarId;

  // Listen for popup postMessage from Xero OAuth callback
  const handleXeroMessage = useCallback((event) => {
    if (event.data?.type === 'xero-connected') {
      setXeroConnecting(false);
      toast('Connected to Xero!', 'success');
      xeroRefetch();
    }
  }, [toast, xeroRefetch]);

  useEffect(() => {
    window.addEventListener('message', handleXeroMessage);
    return () => window.removeEventListener('message', handleXeroMessage);
  }, [handleXeroMessage]);

  async function handleConnectXero() {
    setXeroConnecting(true);
    try {
      const { url } = await api.get('/xero/auth-url');
      const w = 600, h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(url, 'xero-auth', `width=${w},height=${h},left=${left},top=${top}`);
      // Poll for popup close (user cancelled)
      const timer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(timer);
          setXeroConnecting(false);
        }
      }, 500);
    } catch (err) {
      setXeroConnecting(false);
      toast(`Failed to start Xero connection: ${err.message}`, 'error');
    }
  }

  function handleDisconnectXero() {
    if (!confirm('Disconnect Xero? Existing synced invoices will keep their Xero IDs but no new syncs will occur.')) return;
    xeroDisconnect.mutate(undefined, {
      onSuccess: () => toast('Xero disconnected', 'success'),
      onError: (err) => toast(`Failed to disconnect: ${err.message}`, 'error'),
    });
  }

  function handleSetupCalendar() {
    gcalSetup.mutate(undefined, {
      onSuccess: (data) => {
        toast(`RFOS calendar created! Set GCAL_CALENDAR_ID=${data.calendarId} in your server .env and restart.`, 'success');
      },
      onError: (err) => toast(`Setup failed: ${err.message}`, 'error'),
    });
  }

  return (
    <div className="space-y-4">
      <p className="text-text-dim text-sm">
        Connect RFOS to external services.
      </p>

      <div className="space-y-3">
        {INTEGRATIONS.map((integration) => {
          // Special rendering for Xero
          if (integration.id === 'xero') {
            const badgeClasses = xeroConnected
              ? 'bg-[#1e8b4a]/10 border-[#1e8b4a]/30 text-[#1e8b4a]'
              : 'bg-surface-2 border-border text-text-dim';
            const badgeLabel = xeroLoading ? '...' : xeroConnected ? 'Connected' : 'Not Connected';

            return (
              <div
                key={integration.id}
                className="flex items-start gap-4 p-4 bg-surface border border-border rounded"
              >
                <div className="text-text-dim mt-0.5">{integration.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-text-primary">{integration.name}</span>
                    <span className={cn('text-2xs px-1.5 py-0.5 rounded border font-medium', badgeClasses)}>
                      {badgeLabel}
                    </span>
                  </div>
                  <p className="text-xs text-text-dim">{integration.description}</p>
                  {xeroConnected && xeroStatus.orgName && (
                    <p className="text-2xs text-text-dim mt-1">Organization: {xeroStatus.orgName}</p>
                  )}
                  {xeroConnected && xeroAccounts.length > 0 && (
                    <div className="mt-2">
                      <label className="block text-2xs font-semibold text-text-dim mb-1">Revenue Account</label>
                      <select
                        value={xeroStatus.revenueAccountCode || ''}
                        onChange={async (e) => {
                          try {
                            const current = await api.get('/settings');
                            current.xero = { ...(current.xero || {}), revenueAccountCode: e.target.value };
                            await api.put('/settings', { data: current });
                            logAction({
                              action: 'Update Xero Settings',
                              description: `Set Xero revenue account to ${e.target.value || 'Default'}`,
                              details: { revenueAccountCode: e.target.value || null },
                            });
                            xeroRefetch();
                            toast('Revenue account updated', 'success');
                          } catch (err) {
                            toast(`Failed to save: ${err.message}`, 'error');
                          }
                        }}
                        className="w-full px-2 py-1 bg-bg border border-border rounded text-xs text-text-primary"
                      >
                        <option value="">Default (200)</option>
                        {xeroAccounts.map((a) => (
                          <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  {!xeroConnected && (
                    <button
                      onClick={handleConnectXero}
                      disabled={xeroConnecting || xeroLoading}
                      className="px-3 py-1.5 bg-[#1e8b4a] text-white rounded text-xs font-medium hover:bg-[#167a3f] disabled:opacity-50"
                    >
                      {xeroConnecting ? 'Connecting...' : 'Connect to Xero'}
                    </button>
                  )}
                  {xeroConnected && (
                    <button
                      onClick={handleDisconnectXero}
                      disabled={xeroDisconnect.isPending}
                      className="px-3 py-1.5 bg-surface-2 border border-border text-text-dim rounded text-xs font-medium hover:bg-surface-3"
                    >
                      {xeroDisconnect.isPending ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  )}
                </div>
              </div>
            );
          }

          // Special rendering for Google Calendar
          if (integration.id === 'google_calendar') {
            const badgeClasses = gcalConnected
              ? 'bg-[#1e8b4a]/10 border-[#1e8b4a]/30 text-[#1e8b4a]'
              : 'bg-surface-2 border-border text-text-dim';
            const badgeLabel = gcalLoading ? '...' : gcalConnected ? 'Connected' : 'Not Configured';

            return (
              <div
                key={integration.id}
                className="flex items-start gap-4 p-4 bg-surface border border-border rounded"
              >
                <div className="text-text-dim mt-0.5">{integration.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-text-primary">{integration.name}</span>
                    <span className={cn('text-2xs px-1.5 py-0.5 rounded border font-medium', badgeClasses)}>
                      {badgeLabel}
                    </span>
                  </div>
                  <p className="text-xs text-text-dim">{integration.description}</p>
                  {gcalConnected && gcalCalendarId && (
                    <p className="text-2xs text-text-dim mt-1 font-mono">Calendar: {gcalCalendarId}</p>
                  )}
                  {!gcalConnected && !gcalLoading && gcalConfig?.error && (
                    <p className="text-2xs text-orange-500 mt-1">{gcalConfig.error}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  {gcalConnected && !gcalCalendarId && (
                    <button
                      onClick={handleSetupCalendar}
                      disabled={gcalSetup.isPending}
                      className="px-3 py-1.5 bg-[#1e8b4a] text-white rounded text-xs font-medium hover:bg-[#167a3f] disabled:opacity-50"
                    >
                      {gcalSetup.isPending ? 'Creating...' : 'Create RFOS Calendar'}
                    </button>
                  )}
                  {!gcalConnected && (
                    <span className="text-2xs text-text-dim text-right max-w-[140px]">
                      Set GCAL_SERVICE_ACCOUNT_KEY &amp; GCAL_IMPERSONATE_EMAIL in server .env
                    </span>
                  )}
                </div>
              </div>
            );
          }

          // Standard stub rendering for other integrations
          const comingSoon = integration.status === 'coming_soon';
          if (comingSoon && isHidden('settings.comingSoon')) return null;
          const badge = STATUS_BADGE[integration.status];
          return (
            <div
              key={integration.id}
              data-focus={comingSoon ? 'settings.comingSoon' : undefined}
              className="flex items-start gap-4 p-4 bg-surface border border-border rounded"
            >
              <div className="text-text-dim mt-0.5">{integration.icon}</div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-text-primary">
                    {integration.name}
                  </span>
                  <span
                    className={cn(
                      'text-2xs px-1.5 py-0.5 rounded border font-medium',
                      badge.classes
                    )}
                  >
                    {badge.label}
                  </span>
                </div>
                <p className="text-xs text-text-dim">{integration.description}</p>
              </div>

              <button
                disabled
                className="px-3 py-1.5 bg-surface-2 border border-border text-text-dim rounded text-xs font-medium cursor-not-allowed opacity-50"
              >
                Configure
              </button>
            </div>
          );
        })}
      </div>

      <div className="p-3 bg-surface border border-border rounded text-xs text-text-dim">
        Google Calendar is configured via server environment variables.
        Xero connects via OAuth — click Connect to link your organization.
      </div>
    </div>
  );
}
